// Store types aligned with Supabase schema (categories, product_families, sourcing_items, leads, quotation_requests, lookups)

export interface Brand {
  id: string;
  name: string;
  description?: string;
  /** Logo image URL; used as reference when generating product cover images with AI. If missing, only brand name is used in the prompt. */
  logo?: string;
}

export interface Origin {
  id: string;
  name: string;
  description?: string;
}

export interface OriginPort {
  id: string;
  origin_id: string;
  name: string;
  description?: string;
}

export interface LoadingMethod {
  id: string;
  name: string;
  description?: string;
}

/** Contacto de un proveedor (un supplier puede tener varios) */
export interface SupplierContact {
  id: string;
  supplier_id: string;
  name?: string;
  email?: string;
  phone?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Supplier {
  id: string;
  name: string;
  /** Sitio web del proveedor */
  website?: string;
  /** Contactos (nombre, email, teléfono) — cargado con supplier_contacts */
  supplier_contacts?: SupplierContact[];
  created_at?: string;
  updated_at?: string;
}

export interface MoqUnit {
  id: string;
  name: string;
  description?: string;
}

export interface CostIncoterm {
  id: string;
  name: string;
  description?: string;
}

export interface ProductStatus {
  id: string;
  name: string;
  description?: string;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  /** Main/cover image. Use for og:image, SEO and large views. */
  cover_image?: string;
  /** Smaller image for list/grid thumbnails. For link previews use cover_image. */
  thumbnail?: string;
}

export interface ProductFamily {
  id: string;
  family_name: string;
  slug: string;
  /** Main/cover image. Use for og:image, SEO and large views. Thumbnail is derived from this (resize) in the app. */
  showroom_image?: string;
  /** Derived smaller image for list/grid thumbnails only. For link previews use showroom_image. */
  thumbnail?: string;
  /** Secondary images (gallery). */
  secondary_images?: string[];
  marketing_description?: string;
  status_id: string;
}

/**
 * Sourcing item origin: logistics data for a specific origin of a product.
 * One product can be sourced from multiple origins (e.g., China, Spain).
 * Only one origin is active at a time for public display.
 */
export interface SourcingItemOrigin {
  id: string;
  sourcing_item_id: string;
  origin_id: string;
  origin_port_id?: string;
  /** Supplier for this origin (company that provides the product for this load) */
  supplier_id?: string;
  cost_usd?: number;
  cost_incoterm_id?: string;
  loading_method_id?: string;
  units_per_box: number;
  boxes_per_pallet?: number;
  /** Cajas por contenedor (genérico). Pallets por contenedor vienen de constantes por tipo (src/lib/constants.ts). */
  boxes_per_container: number;
  /** Recomendación: 20DV | 40HQ | 20REF | 40REF. Valor inicial del contenedor del mix; no filtro duro. */
  recommended_container_type?: string;
  /** Dimensiones de caja en cm (para cálculo volumétrico de llenado cuando carga a piso). */
  box_length_cm?: number;
  box_width_cm?: number;
  box_height_cm?: number;
  gross_weight_per_box_kg: number;
  moq_value: number;
  moq_unit_id?: string;
  /** Tiempo de producción en días */
  production_time_days?: number;
  /** Forma de pago, ej: "30% deposito, 70% contra inspeccion antes de cargar" */
  payment_terms?: string;
  /** Notas específicas de este origen (públicas: se muestran en tienda/PDF) */
  observaciones?: string;
  /** Notas internas para compra/coste y trato con proveedor. Solo admin; no en tienda ni PDF. */
  internal_notes?: string;
  /** Referencia / código del proveedor para este ítem (interno; para pedir cotización) */
  referencia?: string;
  /** Solo un origen por item puede estar activo (visible en vista pública) */
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Sourcing item: product-level data.
 * Origin-specific logistics data (MOQ, cost, port, etc.) comes from the active SourcingItemOrigin.
 * When fetched via public view, these fields are populated from the active origin.
 * Admin view can access all origins via the item's origins array or separate query.
 */
export interface SourcingItem {
  id: string;
  internal_name: string;
  slug: string;
  brand_id: string | null;
  sales_format: string;
  /** Main/cover image. Use for og:image (Facebook, etc.). Same path format as thumbnail: full URL or Supabase storage path. */
  packshot_image?: string;
  /** Smaller image for list/grid; when present, same path format as packshot_image. Worker uses thumbnail only for WhatsApp og:image (≤600KB). */
  thumbnail?: string;
  /** Secondary images (gallery). */
  secondary_images?: string[];
  barcode_ean?: string;
  status_id: string;

  // === pSEO: structured metadata for programmatic SEO (B2B LATAM) ===
  /** SEO metadata generated by AI or edited manually in admin. Stored as JSONB in DB. */
  seo_metadata?: {
    meta_title?: string;
    meta_description?: string;
    search_intent?: string;
    assigned_ports?: string[];
    target_ports?: string[];
    extra_keywords?: string[];
    /** AI-generated FAQs for product page and FAQPage JSON-LD. */
    ai_faqs?: Array<{ pregunta: string; respuesta: string }>;
    seo_slug?: string;
    /** pSEO config version that generated this metadata (from PseoConfig.version). */
    pseo_version?: number;
    [key: string]: unknown;
  };
  /** Auto-updated on row change (trigger). Used for sitemap lastmod. */
  updated_at?: string;
  /** Previous slug kept for 301 redirects when slug is changed to SEO-friendly Spanish version. */
  old_slug?: string;
  /** Additional previous slugs (e.g. v1 Spanish when upgraded to v2); all redirect 301 to current slug. */
  old_slugs?: string[] | null;

  // === Origin data (from active origin join or deprecated direct fields) ===
  // These fields come from the active SourcingItemOrigin via the public_sourcing_items view
  origin_id?: string;
  origin_port_id?: string;
  supplier_id?: string;
  cost_usd?: number;
  cost_incoterm_id?: string;
  loading_method_id?: string;
  units_per_box?: number;
  boxes_per_pallet?: number;
  boxes_per_container?: number;
  /** Pallets per container (from view: constant by recommended_container_type: 20=11, 40=21, 20 Ref=9, 40 Ref=19) */
  pallets_per_container?: number;
  /** Recommended container: 20DV, 40HQ, 20REF, 40REF */
  recommended_container_type?: string;
  /** Box dimensions in cm (from active origin; for volumetric fill when floor load). */
  box_length_cm?: number;
  box_width_cm?: number;
  box_height_cm?: number;
  gross_weight_per_box_kg?: number;
  moq_value?: number;
  moq_unit_id?: string;
  production_time_days?: number;
  /** Notas públicas (temporada, precio Mariel, mix MIX-XIAMEN/MIX-QINGDAO, etc.). Se muestra en la página de producto. */
  observaciones?: string;
  
  // === Admin-only: all origins for this item ===
  /** All origins for this item (admin only, populated separately) */
  origins?: SourcingItemOrigin[];
}

/**
 * Store lead: authenticated store user / client (quotation requests).
 * For cold outreach prospects see CrmLead and companies (B2B CRM).
 */
export interface Lead {
  id: string;
  email: string;
  name?: string;
  phone?: string;
  company?: string;
  country?: string;
  city?: string;
  /** WhatsApp for contact (Fase 6). */
  whatsapp?: string;
  /** Set when client has verified and set password (Supabase auth user id). */
  auth_user_id?: string;
  created_at: string;
  /** Admin-only notes for analyzing the lead (observaciones). */
  observations?: string | null;
  /** Future: link to companies.id after migrating embedded company data from leads. */
  company_id?: string | null;
}

/** B2B company: prospect (cold outreach) or active_client. Centralizes company data for CRM and store. */
export interface Company {
  id: string;
  name: string;
  domain?: string | null;
  country?: string | null;
  business_status: 'prospect' | 'active_client';
  created_at: string;
}

/** Cold outreach prospect/contact (CRM); distinct from store Lead. Used by AI assistant and Email Worker. */
export interface CrmLead {
  id: string;
  company_id: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  linkedin_url?: string | null;
  outreach_status: 'uncontacted' | 'contacted' | 'replied' | 'converted' | 'bounced';
  created_at: string;
}

/** Email thread message per crm_lead (Cloudflare Email Worker). */
export interface CrmMessage {
  id: string;
  crm_lead_id: string;
  body?: string | null;
  direction: 'inbound' | 'outbound';
  message_type: 'external_lead' | 'internal_admin_instruction' | 'internal_assistant_log';
  created_at: string;
}

/** Pre-accept: draft | pending | en_revision | cotizado | finished. Post-accept: pending_deposit_payment | in_process | shipped | pending_final_payment | arriving_soon | delivered. */
export type QuotationRequestStatus =
  | 'draft'
  | 'pending'
  | 'en_revision'
  | 'cotizado'
  | 'finished'
  | 'pending_deposit_payment'
  | 'in_process'
  | 'shipped'
  | 'pending_final_payment'
  | 'arriving_soon'
  | 'delivered';

const VALID_STATUSES: QuotationRequestStatus[] = [
  'draft',
  'pending',
  'en_revision',
  'cotizado',
  'finished',
  'pending_deposit_payment',
  'in_process',
  'shipped',
  'pending_final_payment',
  'arriving_soon',
  'delivered',
];

/**
 * Normalize status from DB (e.g. old rows or null) to a valid QuotationRequestStatus.
 * Unknown or null → 'draft' so admin UI and Select don't break.
 */
export function normalizeQuotationRequestStatus(value: unknown): QuotationRequestStatus {
  if (typeof value === 'string' && VALID_STATUSES.includes(value as QuotationRequestStatus)) {
    return value as QuotationRequestStatus;
  }
  return 'draft';
}

/** Document types for quotation_request_documents (internal keys in English). */
export type QuotationRequestDocumentType =
  | 'pdf_quotation'
  | 'pdf_proforma_invoice'
  | 'deposit_receipt'
  | 'shipping_documents'
  | 'balance_payment_receipt';

export interface QuotationRequestDocument {
  id: string;
  quotation_request_id: string;
  document_type: QuotationRequestDocumentType;
  file_url: string;
  file_name?: string | null;
  uploaded_by_admin: boolean;
  created_at: string;
}

export interface QuotationRequest {
  id: string;
  created_at: string;
  /** Set when user sends request or registers; null for anonymous cart requests */
  lead_id?: string;
  specifications?: string;
  /** Pre-accept and post-accept statuses (see QuotationRequestStatus). */
  status?: QuotationRequestStatus;
  /** Bank account for deposit/balance; shown when status is pending_deposit_payment (or pending_final_payment). */
  bank_account_id?: string | null;
  /** ETD at origin port (admin-filled when shipped). */
  etd_origin_port?: string | null;
  /** ETA at destination port (arriving_soon / delivered). */
  eta_destination_port?: string | null;
  /** Legacy: flat list of items; may be derived from blocks */
  items: QuotationRequestItem[];
  /** General + mix blocks (same structure as quotation_documents.blocks). Request is created on first "add to quotation". */
  blocks?: QuotationDocBlocks;
  /** Identifies anonymous cart (cookie/session) before lead_id is set */
  anonymous_id?: string;
  /** Incoterm requested by customer (e.g. CIF, DDP) */
  incoterm?: string;
  /** Port or delivery address for the quotation */
  destination_port_or_address?: string;
  /** WhatsApp for this request (Fase 6). */
  whatsapp?: string;
  /** Unique token for shared catalog link. If set, catalog viewable at /store/catalog/:share_token. If lead_id is set, only that lead can open. */
  share_token?: string | null;
  /** When lead responds to cotizado: 'accepted' | 'rejected'. */
  outcome?: 'accepted' | 'rejected' | null;
  /** When the lead accepted or rejected the quotation. */
  responded_at?: string | null;
  /** Comment from lead when requesting changes (status cotizado → en_revision). */
  revision_request_comment?: string | null;
  /** Document-level fields for unified admin quotation (same as quotation_documents). */
  addressee_company?: string | null;
  addressee_contact?: string | null;
  payment_type?: string | null;
  payment_method?: string | null;
  observaciones?: string | null;
  final_price_text?: string | null;
  /** Document display for digital offer (shared catalog / acceptance view). */
  document_title?: string | null;
  header_image_url?: string | null;
  seal_image_url?: string | null;
  final_message?: string | null;
  show_final_message?: boolean | null;
  /** Populated when bank_account_id is set (GET response). */
  bank_account?: CompanyBankAccount | null;
  /** Documents for this request (GET response). */
  documents?: QuotationRequestDocument[] | null;
}

export interface QuotationRequestItem {
  sourcing_item_id: string;
  quantity: number;
  moq_unit?: string;
  name?: string;
  slug?: string;
  /** Target price per unit in USD (optional, from customer) */
  target_price_per_unit?: number;
  /** Reference price per line (admin-filled; shown in shared catalog when status allows) */
  price_reference?: number;
}

// === Company settings and bank accounts ===

/** pSEO configuration stored in company_settings.pseo_config JSONB. Editable from admin. */
export interface PseoConfig {
  system_prompt?: string;
  /** Master port list by region. Each entry: { region, ports[] } */
  master_ports?: Array<{ region: string; ports: string[] }>;
  base_keywords?: string[];
  model?: string;
  batch_size?: number;
  delay_ms?: number;
  /** Monotonically increasing version — bumped by admin when prompt/ports/logic change. Stamped into each item's seo_metadata.pseo_version. */
  version?: number;
}

export interface CompanySettings {
  id: string;
  legal_name: string;
  entity_code?: string | null;
  address?: string | null;
  header_image_url?: string | null;
  seal_image_url?: string | null;
  pseo_config?: PseoConfig;
  created_at: string;
  updated_at: string;
}

export interface CompanyBankAccount {
  id: string;
  label: string;
  bank_name?: string | null;
  account_holder?: string | null;
  account_number?: string | null;
  iban?: string | null;
  swift?: string | null;
  currency?: string | null;
  account_type?: string | null;
  details?: string | null;
  /** Address of the beneficiary bank (for wire instructions). */
  beneficiary_bank_address?: string | null;
  /** Intermediary / correspondent bank (for international transfers). */
  intermediary_bank_name?: string | null;
  intermediary_bank_address?: string | null;
  intermediary_bank_swift?: string | null;
  created_at: string;
  updated_at: string;
}

// === Quotation Documents (admin-generated catalogs/quotations) ===

/** Container type for blocks: 20/40 dry or 20/40 refer (refrigerated). */
export type ContainerType = 20 | 40 | '20REF' | '40REF';

/** Row in the "Productos a Contenedor Completo" block: one product = full container(s) */
export interface GeneralBlockRow {
  sourcing_item_id: string;
  sourcing_item_origin_id?: string;
  /** Number of containers (>= minimum for that product) */
  quantity_containers: number;
  /** Container type: 20ft, 40ft, or 20/40 refer */
  container_type: ContainerType;
  /** Incoterm for this line (e.g. CIF, DDP, FOB) */
  incoterm?: string;
  /** Destination port or address */
  destination?: string;
  /** Estimated production time in days */
  estimated_production_days?: number;
  /** Estimated arrival time in days */
  estimated_arrival_days?: number;
  /** Approximate price per box (referential, final under firm order) */
  price_per_box_approx?: number;
}

/** The "Productos a Contenedor Completo" block */
export interface GeneralBlock {
  type: 'general';
  rows: GeneralBlockRow[];
  /** Optional customer instructions for this block (e.g. loading preferences). */
  specification?: string;
}

/** User choice per line: palletized load or floor load. When not set, use product default (loading_method). */
export type RowLoadType = 'pallets' | 'floor';

/** Row in a mix container block: either boxes (mix) or full container(s). */
export interface MixBlockRow {
  sourcing_item_id: string;
  sourcing_item_origin_id?: string;
  /** Number of boxes (>= MOQ) when load is boxes/pallets. Omit when quantity_containers is used. */
  boxes?: number;
  /** Full-container row: number of containers (>= MOQ). When set, container_type is required and block is "1 product = full space". */
  quantity_containers?: number;
  /** Full-container row: container type (fixed, not editable in UI). */
  container_type?: ContainerType;
  /** Approximate price per box */
  price_per_box_approx?: number;
  /** User override: this line is palletized or floor-loaded. If omitted, use product's loading_method. */
  row_load_type?: RowLoadType;
  /** Full-container row: estimated production/arrival days (admin). */
  estimated_production_days?: number;
  estimated_arrival_days?: number;
}

/** Load type for mix blocks: floor-loaded (boxes) or palletized (pallets). Same mix block only shares one type. */
export type MixLoadType = 'boxes' | 'pallets';

/** A "Mix" block: products sharing same origin/port/supplier in one container */
export interface MixBlock {
  type: 'mix';
  /** Section name, e.g. "Sugerencia mix para optimización de costes" */
  name?: string;
  /** Container type: 20ft, 40ft, or 20/40 refer */
  container_type: ContainerType;
  /** Number of containers */
  num_containers: number;
  /** All products in this mix must share these */
  origin_id: string;
  origin_port_id?: string;
  supplier_id?: string;
  /** When set (e.g. by store cart): mix is either boxes or pallets; no mixing both in same block */
  load_type?: MixLoadType;
  /** Estimated production time in days (shown in mix block header) */
  estimated_production_days?: number;
  /** Estimated arrival/travel time to destination in days (shown in mix block header) */
  estimated_arrival_days?: number;
  /** Optional customer instructions for this mix (e.g. mix floor + pallets, fill gaps, loose boxes on pallets). */
  specification?: string;
  rows: MixBlockRow[];
}

/** Combined blocks structure stored in quotation_documents.blocks */
export interface QuotationDocBlocks {
  general: GeneralBlock;
  mix: MixBlock[];
}

/** PDF version entry */
export interface PdfVersion {
  url: string;
  generated_at: string;
}

/** Quotation document: admin-generated catalog/quotation with blocks and PDF */
export interface QuotationDocument {
  id: string;
  quotation_request_id?: string;
  title: string;
  header_image_url?: string;
  /** Seal image URL for PDF closing (selected from quotation_seal_images gallery) */
  seal_image_url?: string;
  /** Optional final message with asterisco (e.g. price disclaimer); shown only if show_final_message is true */
  final_message?: string;
  /** When true, final_message is rendered in the PDF */
  show_final_message?: boolean;
  blocks: QuotationDocBlocks;
  addressee_company?: string;
  addressee_contact?: string;
  /** Incoterm for the whole quotation (e.g. CIF, DDP, FOB) */
  incoterm?: string;
  /** Destination port or address */
  destination?: string;
  final_price_text?: string;
  payment_type?: string;
  payment_method?: string;
  observaciones?: string;
  last_pdf_url?: string;
  pdf_versions: PdfVersion[];
  /** When admin approved this doc for client download; client can download until download_expires_at */
  approved_at?: string;
  /** PDF URL for client download (valid until download_expires_at, typically 48h after approval) */
  approved_pdf_url?: string;
  /** After this time client can no longer download; usually approved_at + 48h */
  download_expires_at?: string;
  /** If false, admin has locked editing (e.g. while filling prices); client cannot edit */
  editable_by_client?: boolean;
  created_at: string;
  updated_at: string;
}

/** Reusable header image for quotation PDFs */
export interface QuotationHeaderImage {
  id: string;
  name: string;
  url: string;
  created_at: string;
}

/** Reusable seal image for quotation PDF closing */
export interface QuotationSealImage {
  id: string;
  name: string;
  url: string;
  created_at: string;
}

// --- Catalogue conversion wizard (provider catalogue → families/products) ---

/** One block returned by AI digitalize: one family with headers and rows (products/formats). */
export interface RawCatalogueBlock {
  family_name: string;
  /** AI-generated short description of the product family (step 0→1). */
  family_description?: string;
  headers: string[];
  rows: Record<string, string>[];
}

/** Column mapping: our field name → list of provider column names (for concatenation). */
export type ColumnMapping = Record<string, string[]>;

/** Normalized product (no DB IDs); product + origin-like fields from row + Step 0 config. */
export interface ConvertedProduct {
  internal_name: string;
  sales_format: string;
  cost_usd?: number;
  units_per_box?: number;
  boxes_per_pallet?: number;
  boxes_per_container?: number;
  gross_weight_per_box_kg?: number;
  moq_value?: number;
  /** MOQ unit lookup — filled in Step 3. */
  moq_unit_id?: string;
  observaciones?: string;
  /** Info interna (solo admin; no en tienda ni PDF). */
  internal_notes?: string;
  /** Origin-level: incoterm (text from catalogue; resolved to id in Step 3 or on save). */
  cost_incoterm?: string;
  cost_incoterm_id?: string;
  /** Tiempo de producción en días. */
  production_time_days?: number;
  /** Forma de pago. */
  payment_terms?: string;
  /** Referencia / código del proveedor. */
  referencia?: string;
  /** URL de imagen del catálogo; al guardar en DB se descarga, se sube a Storage y se genera thumbnail (como en edit product). */
  packshot_image?: string;
  [key: string]: unknown;
}

/** Normalized family + products (no DB IDs yet). */
export interface ConvertedBlock {
  family: {
    family_name: string;
    slug: string;
    /** Optional description for this family (from mapping or Step 3). */
    family_description?: string;
    [key: string]: unknown;
  };
  products: ConvertedProduct[];
}

/** Per-block family decision: new or existing family id. */
export interface FamilyMatchDecision {
  blockIndex: number;
  familyDecision: 'new' | string; // string = existing_family_id (uuid)
}

/** Per-product decision: new or existing item id. */
export interface ProductMatchDecision {
  blockIndex: number;
  rowIndex: number;
  productDecision: 'new' | string; // string = existing_item_id (uuid)
}

export interface MatchDecisions {
  families: FamilyMatchDecision[];
  products: ProductMatchDecision[];
}

/** Draft row for the catalogue conversion wizard. */
export interface CatalogueConversionDraft {
  id: string;
  created_at: string;
  updated_at: string;
  step: number;
  category_id: string | null;
  supplier_id: string | null;
  brand_id: string | null;
  origin_id: string | null;
  origin_port_id: string | null;
  /** Loading method applied to new sourcing_item_origins when saving to DB. */
  loading_method_id: string | null;
  /** Optional default marketing_description for new product_families. */
  family_description: string | null;
  user_prompt: string | null;
  file_type: string | null;
  file_url: string | null;
  file_base64: string | null;
  raw_blocks: RawCatalogueBlock[] | null;
  column_mapping: ColumnMapping | null;
  converted_blocks: ConvertedBlock[] | null;
  match_decisions: MatchDecisions | null;
  saved_family_ids: string[] | null;
  saved_item_ids: string[] | null;
  finalized_at: string | null;
}
